import React, { useEffect, useState } from 'react';
import apiService from '../../services/api';
import './ManageBhaktiDarshan.css';

const initialForm = {
  shrineId: '',
  name: '',
  location: '',
  streamUrl: '',
  thumbnail: '',
  description: '',
  sortOrder: 0,
  isActive: true
};

const ManageBhaktiDarshan = () => {
  const [shrines, setShrines] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [editingShrineId, setEditingShrineId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchShrines = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await apiService.getAdminBhaktiDarshanShrines();
      setShrines(response?.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load Bhakti Darshan shrines.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchShrines();
  }, []);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    setEditingShrineId('');
    setError('');
  };

  const startEdit = (shrine) => {
    setError('');
    setEditingShrineId(shrine.shrineId || shrine.id);
    setFormData({
      shrineId: shrine.shrineId || shrine.id || '',
      name: shrine.name || '',
      location: shrine.location || '',
      streamUrl: shrine.streamUrl || '',
      thumbnail: shrine.thumbnail || '',
      description: shrine.description || '',
      sortOrder: shrine.sortOrder ?? 0,
      isActive: shrine.isActive !== false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      if (editingShrineId) {
        await apiService.updateAdminBhaktiDarshanShrine(editingShrineId, formData);
      } else {
        await apiService.createAdminBhaktiDarshanShrine(formData);
      }
      resetForm();
      await fetchShrines();
    } catch (err) {
      setError(err.message || `Failed to ${editingShrineId ? 'update' : 'create'} shrine.`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleQuickStatusChange = async (shrine, isActive) => {
    setError('');
    try {
      await apiService.updateAdminBhaktiDarshanShrine(shrine.shrineId || shrine.id, { isActive });
      await fetchShrines();
    } catch (err) {
      setError(err.message || 'Failed to update shrine status.');
    }
  };

  const handleDelete = async (shrineId) => {
    if (!window.confirm('Delete this Bhakti Darshan shrine?')) return;

    setError('');
    try {
      await apiService.deleteAdminBhaktiDarshanShrine(shrineId);
      if (editingShrineId === shrineId) resetForm();
      await fetchShrines();
    } catch (err) {
      setError(err.message || 'Failed to delete shrine.');
    }
  };

  return (
    <div className="manage-darshan-container">
      <div className="header-section">
        <h2>Manage Bhakti Darshan</h2>
      </div>

      {error ? <div className="error-message">Error: {error}</div> : null}

      <form className="darshan-form" onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="name">Shrine Name</label>
            <input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g. Kashi Vishwanath Temple"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="shrineId">Shrine ID</label>
            <input
              id="shrineId"
              name="shrineId"
              value={formData.shrineId}
              onChange={handleInputChange}
              placeholder="auto-generated from name if empty"
              disabled={Boolean(editingShrineId)}
            />
          </div>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="location">Location</label>
            <input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="e.g. Varanasi, Uttar Pradesh"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="sortOrder">Sort Order</label>
            <input
              id="sortOrder"
              name="sortOrder"
              type="number"
              value={formData.sortOrder}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="streamUrl">Live Stream URL</label>
          <input
            id="streamUrl"
            name="streamUrl"
            value={formData.streamUrl}
            onChange={handleInputChange}
            placeholder="YouTube watch/embed URL or another live stream URL"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="thumbnail">Thumbnail URL</label>
          <input
            id="thumbnail"
            name="thumbnail"
            value={formData.thumbnail}
            onChange={handleInputChange}
            placeholder="https://..."
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            placeholder="Optional internal note or public description"
          />
        </div>

        <div className="darshan-form-footer">
          <label className="checkbox-row">
            <input
              name="isActive"
              type="checkbox"
              checked={formData.isActive}
              onChange={handleInputChange}
            />
            Active on Bhakti Darshan page
          </label>

          <div className="form-action-buttons">
            {editingShrineId ? (
              <button type="button" className="action-btn" onClick={resetForm}>
                Cancel
              </button>
            ) : null}
            <button type="submit" className="btn-primary-admin" disabled={isSaving}>
              {isSaving ? 'Saving...' : editingShrineId ? 'Update Shrine' : 'Add Shrine'}
            </button>
          </div>
        </div>
      </form>

      <div className="entries-table-wrapper">
        <table className="entries-table">
          <thead>
            <tr>
              <th>Shrine</th>
              <th>Live URL</th>
              <th>Status</th>
              <th>Sort</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="5" className="no-results">Loading shrines...</td>
              </tr>
            ) : shrines.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-results">No shrines found.</td>
              </tr>
            ) : (
              shrines.map((shrine) => {
                const shrineId = shrine.shrineId || shrine.id;
                return (
                  <tr key={shrineId}>
                    <td>
                      <div className="shrine-cell">
                        <img src={shrine.thumbnail} alt={shrine.name} />
                        <div>
                          <strong>{shrine.name}</strong>
                          <p className="entry-content-preview">{shrine.location}</p>
                          <span className="shrine-id">{shrineId}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <a className="darshan-url" href={shrine.streamUrl} target="_blank" rel="noreferrer">
                        {shrine.streamUrl}
                      </a>
                    </td>
                    <td>
                      <span className={`status-badge ${shrine.isActive ? 'active' : 'inactive'}`}>
                        {shrine.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td>{shrine.sortOrder ?? 0}</td>
                    <td>
                      <div className="action-cell">
                        <button type="button" className="action-btn" onClick={() => startEdit(shrine)}>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="action-btn"
                          onClick={() => handleQuickStatusChange(shrine, !shrine.isActive)}
                        >
                          {shrine.isActive ? 'Disable' : 'Activate'}
                        </button>
                        <button type="button" className="action-btn btn-delete" onClick={() => handleDelete(shrineId)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageBhaktiDarshan;
